import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Star, ThumbsUp, Home, Sparkles } from 'lucide-react';

export const PaymentSuccessPage = () => {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            >
              <div
                className="w-3 h-3 rotate-45"
                style={{
                  backgroundColor: ['#00bcd4', '#4ade80', '#fbbf24', '#f472b6', '#818cf8'][Math.floor(Math.random() * 5)],
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <section className="relative py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-green-500/10" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success Icon */}
            <div className="relative inline-block mb-8">
              <div className="w-32 h-32 rounded-full bg-green-500/20 flex items-center justify-center mx-auto animate-pulse">
                <div className="w-24 h-24 rounded-full bg-green-500/30 flex items-center justify-center">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
              </div>
              <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-yellow-400 animate-bounce" />
              <Sparkles className="absolute -bottom-2 -left-2 w-6 h-6 text-primary animate-bounce" style={{ animationDelay: '0.5s' }} />
            </div>

            {/* Success Message */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Payment <span className="text-gradient-cyan">Successful!</span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-4">
              Thank you for your payment.
            </p>
            
            <p className="text-lg text-muted-foreground mb-12">
              We've received your payment and our team is ready to deliver exceptional glass solutions for your project. You'll receive a confirmation email shortly.
            </p>

            {/* Review Request Card */}
            <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent mb-8">
              <CardContent className="pt-8 pb-8">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                
                <h2 className="text-2xl font-bold mb-4">
                  Enjoying Your Experience?
                </h2>
                
                <p className="text-muted-foreground mb-6">
                  We'd love to hear from you! Your review helps other homeowners find quality glass solutions and means the world to our small business.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg"
                    asChild
                  >
                    <a 
                      href="https://google.com/maps/place//data=!4m3!3m2!1s0x4054a314f6086dbd:0xd466bda40fe0e836!12e1?source=g.page.m._&laa=merchant-review-solicitation" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Review on Google
                    </a>
                  </Button>
                  
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white shadow-lg"
                    asChild
                  >
                    <a 
                      href="https://www.facebook.com/comalglass/reviews" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Review on Facebook
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Thank You Note */}
            <div className="bg-muted/50 rounded-lg p-6 mb-8">
              <ThumbsUp className="w-10 h-10 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground italic">
                "Thank you for choosing Comal Glass Company! We're a family-owned business and every customer means the world to us. We can't wait to transform your space with beautiful glass."
              </p>
              <p className="text-sm text-primary mt-2 font-semibold">— The Comal Glass Team</p>
            </div>

            {/* Return Home Button */}
            <Button variant="outline" size="lg" asChild>
              <Link to="/">
                <Home className="mr-2 h-5 w-5" />
                Return to Homepage
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Add confetti keyframe animation */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccessPage;
